import React from 'react';
import { Avatar } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetDisputeReportByIdQuery } from '@/store/services/reports';
import Status from '@/components/DisputeDetails.tsx/atoms/Status';
import { DetailSection } from '../SharedComponents/DetailsSection';
import UserInfo from '../SharedComponents/UserInfo';
import ReportDrawer from '../SharedComponents/ReportDrawer';
import { usePublicState } from '@/store/slices/publicSlice';
import Button from '@/components/atoms/Button';
import { useNavigate } from 'react-router-dom';

export interface RequestStatus {
  id: string;
  code: string;
  name: string;
}

export interface RequestInfo {
  status: RequestStatus;
  serial: string;
  requestBudget: number;
  requestCommission: number;
  requestReason: string;
  requestDate: string;
  orderType: string;
  orderMethod: string;
  orderId: string;
  estDelivery: string | null;
}

export interface UserPartyInfo {
  image: string | null;
  name: string;
  email: string;
  phone: string;
}

export interface DisputeDetailsData {
  requestInfo: RequestInfo;
  sellerInfo: UserPartyInfo;
  buyerInfo: UserPartyInfo;
}

interface RequestDetailsDrawerProps {
  listOfIds: string[];
}

export const RequestDetailsDrawer: React.FC<RequestDetailsDrawerProps> = ({
  listOfIds,
}) => {
  const { modalId } = usePublicState();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(modalId));

  const { data: reportDetails, isFetching } = useGetDisputeReportByIdQuery(
    modalId as string,
    { skip: !isDrawerOpen }
  );

  const response = reportDetails?.payload;

  const requestInfoItems = [
    {
      label: t('reports.disputeStatus', 'Dispute status'),
      value: response?.requestInfo?.status ? (
        <Status text={response?.requestInfo?.status?.name} variant="info" />
      ) : (
        '-'
      ),
    },
    {
      label: t('reports.orderBudget', 'Order budget'),
      value: response?.requestInfo?.requestBudget || '-',
    },
    {
      label: t('reports.requestReason', 'Request reason'),
      value: response?.requestInfo?.requestReason || '-',
    },
    {
      label: t('reports.requestCommission', 'Request commission'),
      value: response?.requestInfo?.requestCommission || '-',
    },

    {
      label: t('reports.requestDate', 'Request date'),
      value: response?.requestInfo?.requestDate
        ? dayjs(response?.requestInfo?.requestDate).isValid()
          ? dayjs(response?.requestInfo?.requestDate).format('DD MMM YYYY')
          : '-'
        : '-',
    },
    {
      label: t('reports.orderId', 'Order ID'),
      value: response?.requestInfo?.orderId || '-',
    },
    {
      label: t('reports.disputeId', 'Dispute ID'),
      value: response?.requestInfo?.serial || '-',
    },
    {
      label: t('reports.estDelivery', 'Est. Delivery'),
      value: response?.requestInfo?.estDelivery || '-',
    },
    {
      label: t('reports.orderType', 'Order type'),
      value: response?.requestInfo?.orderType || '-',
    },
    {
      label: t('reports.orderMethod', 'Order method'),
      value: response?.requestInfo?.orderMethod || '-',
    },
  ];

  return (
    <ReportDrawer
      title={t('reports.requestDetails', 'Request details')}
      listOfIds={listOfIds || []}
    >
      <div className="space-y-6">
        {/* Request Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.requestInformation', 'Request information')}
          items={requestInfoItems}
        />

        {/* Seller Information Section */}
        <UserInfo
          loading={isFetching}
          sectionTitle={t('reports.sellerInformation', 'Seller information')}
          name={response?.sellerInfo?.name || ''}
          email={response?.sellerInfo?.email || ''}
          phone={response?.sellerInfo?.phone || ''}
          image={response?.sellerInfo?.image || ''}
          countryCode={response?.sellerInfo?.countryCode || ''}
        />

        {/* Buyer Information Section */}
        <UserInfo
          loading={isFetching}
          sectionTitle={t('reports.buyerInformation', 'Buyer information')}
          name={response?.buyerInfo?.name || ''}
          email={response?.buyerInfo?.email || ''}
          phone={response?.buyerInfo?.phone || ''}
          image={response?.buyerInfo?.image || ''}
          countryCode={response?.buyerInfo?.countryCode || ''}
        />

        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            navigate(`/dispute/${modalId}`);
          }}
        >
          {t('reports.viewDisputeDetails', 'View dispute details')}
        </Button>
      </div>
    </ReportDrawer>
  );
};
